import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, CircularProgress } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ReservationService from "../../../api/ReservationService";
import "./ReservationV2.css";

/* ── Constants ───────────────────────────────────────────── */
const ALL_SLOTS = [
  "0:00", "1:30", "7:30", "9:00", "10:30",
  "12:00", "13:30", "15:00", "16:30", "18:00",
  "19:30", "21:00", "22:30",
];

const DAY_SHORT = ["Dim", "Lun", "Mar", "Mer", "Jeu", "Ven", "Sam"];
const DAY_LONG  = ["Dimanche","Lundi","Mardi","Mercredi","Jeudi","Vendredi","Samedi"];
const MONTHS_FR = [
  "janvier","février","mars","avril","mai","juin",
  "juillet","août","septembre","octobre","novembre","décembre",
];

const STRIP_SIZE = 7;

/* ── Helpers ─────────────────────────────────────────────── */
function formatDateKey(date) {
  return (
    date.getFullYear() + "-" +
    String(date.getMonth() + 1).padStart(2, "0") + "-" +
    String(date.getDate()).padStart(2, "0")
  );
}

function sameDay(a, b) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function addDays(date, n) {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d;
}

function formatSlotDisplay(slot) {
  const [h, m] = slot.split(":");
  return `${h.padStart(2, "0")}:${m.padStart(2, "0")}`;
}

function addNinetyMin(slot) {
  const [h, m] = slot.split(":").map(Number);
  const total = h * 60 + m + 90;
  return `${String(Math.floor(total / 60) % 24).padStart(2, "0")}:${String(total % 60).padStart(2, "0")}`;
}

/* Extract slot key from ISO start string — same convention as existing app:
   "T07:30:..." → Number("07") → 7 → "7:30" */
function extractSlotKey(isoString) {
  const part = isoString.split("T")[1];
  return `${Number(part.split(":")[0])}:${part.split(":")[1]}`;
}

function isSlotPast(slot, today, date) {
  if (!sameDay(date, today)) return false;
  const [h, m] = slot.split(":").map(Number);
  const t = new Date();
  t.setHours(h, m, 0, 0);
  return t < today;
}

/* ── Component ───────────────────────────────────────────── */
export default function ReservationV2() {
  const navigate = useNavigate();
  const today = useMemo(() => { const d = new Date(); d.setSeconds(0,0); return d; }, []);

  const [stripStart, setStripStart]     = useState(0);
  const [selectedDate, setSelectedDate] = useState(today);
  const [terrains, setTerrains]         = useState([]);
  const [viewLimitDate, setViewLimitDate] = useState(null);
  const [slotMap, setSlotMap]           = useState({});
  const [loading, setLoading]           = useState(true);

  /* ── Init ─────────────────────────────────────────────── */
  useEffect(() => {
    Promise.all([
      ReservationService.getTerrain(),
      ReservationService.getReservationDateViewLimit(),
    ])
      .then(([tRes, lRes]) => {
        setTerrains(tRes.data);
        setViewLimitDate(new Date(lRes.data.data));
      })
      .catch(() => toast.error("Erreur lors du chargement.", { theme: "light" }));
  }, []);

  /* ── Fetch availability ───────────────────────────────── */
  useEffect(() => {
    if (terrains.length === 0) return;
    fetchAvailability(selectedDate, terrains);
  }, [selectedDate, terrains]);

  const fetchAvailability = useCallback(async (date, terrainList) => {
    setLoading(true);
    const dateStr = formatDateKey(date);

    const results = await Promise.all(
      terrainList.map((t) =>
        ReservationService.getReservationsByDate(dateStr, t._id)
          .then((r) => ({ terrain: t, reservations: r.data }))
          .catch(() => ({ terrain: t, reservations: [] }))
      )
    );

    /* Build slotMap: { slotKey → { freeCourts, courts[] } }
       A court is "free" when it has NO reservation for this slot.
       availablePlaces tracks player spots inside an existing reservation. */
    const map = {};
    ALL_SLOTS.forEach((slot) => {
      if (isSlotPast(slot, today, date)) return;

      const courts = results.map(({ terrain, reservations }) => {
        const existing = reservations.find((r) => extractSlotKey(r.start) === slot);
        return {
          _id: terrain._id,
          name: terrain.name,
          free: !existing,
          availablePlaces: existing ? existing.availablePlaces : 4,
          reservationId: existing?._id ?? null,
        };
      });

      const freeCourts = courts.filter((c) => c.free).length;
      map[slot] = { courts, freeCourts };
    });

    setSlotMap(map);
    setLoading(false);
  }, [today]);

  /* ── Date strip ──────────────────────────────────────── */
  const maxStripStart = useMemo(() => {
    if (!viewLimitDate) return 30;
    return Math.max(
      0,
      Math.ceil((viewLimitDate - today) / 86400000) - STRIP_SIZE + 1
    );
  }, [viewLimitDate, today]);

  const stripDates = useMemo(() =>
    Array.from({ length: STRIP_SIZE }, (_, i) => addDays(today, stripStart + i)),
    [stripStart, today]
  );

  /* ── Slot tap → navigate to booking page ─────────────── */
  const handleSlotTap = (slot) => {
    const data = slotMap[slot];
    if (!data || data.freeCourts === 0) return;
    navigate(
      `/reservation-v2/book?date=${formatDateKey(selectedDate)}&slot=${slot}`
    );
  };

  /* ── Badge logic ─────────────────────────────────────── */
  function slotStatus(freeCourts) {
    if (freeCourts === 0) return "full";
    return "available";
  }

  const selectedDateLabel =
    `${DAY_LONG[selectedDate.getDay()]} ${selectedDate.getDate()} ${MONTHS_FR[selectedDate.getMonth()]} ${selectedDate.getFullYear()}`;

  /* ── Render ──────────────────────────────────────────── */
  return (
    <>
      <div className="rv2-page">
        <div className="rv2-container">

          {/* Minimal page header — no wasted space */}
          <div className="rv2-page-header">
            <span className="rv2-page-header-icon">🎾</span>
            <div>
              <h2 className="rv2-page-header-title">Réservations</h2>
              <p className="rv2-page-header-sub">Choisissez une date et un créneau</p>
            </div>
          </div>

          {/* ── Date strip ──────────────────────────────── */}
          <div className="rv2-date-card">
            <div className="rv2-section-label">Choisissez une date</div>
            <div className="rv2-date-strip-row">
              <Button
                variant="outlined"
                className="rv2-date-nav"
                onClick={() => setStripStart((s) => Math.max(0, s - STRIP_SIZE))}
                disabled={stripStart === 0}
                size="small"
              >
                <ChevronLeftIcon fontSize="small" />
              </Button>

              <div className="rv2-date-strip">
                {stripDates.map((date, i) => {
                  const isToday    = sameDay(date, today);
                  const isSelected = sameDay(date, selectedDate);
                  const isPast     = date < today && !isToday;
                  return (
                    <div
                      key={i}
                      className={`rv2-date-btn ${isSelected ? "rv2-date-btn--active" : ""}`}
                      style={{ cursor: isPast ? "not-allowed" : "pointer", opacity: isPast ? 0.38 : 1 }}
                      onClick={() => !isPast && setSelectedDate(new Date(date))}
                    >
                      <span className="rv2-day-name">{DAY_SHORT[date.getDay()]}</span>
                      <span className="rv2-day-num">{date.getDate()}</span>
                      {isToday && <span className="rv2-date-today-dot" />}
                    </div>
                  );
                })}
              </div>

              <Button
                variant="outlined"
                className="rv2-date-nav"
                onClick={() => setStripStart((s) => Math.min(maxStripStart, s + STRIP_SIZE))}
                disabled={stripStart >= maxStripStart}
                size="small"
              >
                <ChevronRightIcon fontSize="small" />
              </Button>
            </div>
            <div className="rv2-selected-date-label">{selectedDateLabel}</div>
          </div>

          {/* ── Slot grid ───────────────────────────────── */}
          <div className="rv2-section-label">Créneaux disponibles</div>

          {loading ? (
            <div className="rv2-loading">
              <CircularProgress size="2.5rem" sx={{ color: "#1a5689" }} />
            </div>
          ) : !ALL_SLOTS.some((s) => slotMap[s] && slotStatus(slotMap[s].freeCourts) !== "full") ? (
            <div className="rv2-empty">
              <div className="rv2-empty-emoji">🎾</div>
              <div className="rv2-empty-text">Aucun créneau disponible ce jour</div>
            </div>
          ) : (
            <div className="rv2-slot-grid">
              {ALL_SLOTS.map((slot) => {
                const data = slotMap[slot];
                if (!data) return null;
                const status = slotStatus(data.freeCourts);
                if (status === "full") return null;

                return (
                  <div
                    key={slot}
                    role="button"
                    tabIndex={status === "full" ? -1 : 0}
                    aria-disabled={status === "full"}
                    className={`rv2-slot-card rv2-slot-card--${status}`}
                    onClick={() => handleSlotTap(slot)}
                    onKeyDown={(e) => e.key === "Enter" && handleSlotTap(slot)}
                  >
                    <div className="rv2-slot-time">{formatSlotDisplay(slot)}</div>
                    <div className="rv2-slot-range">
                      {formatSlotDisplay(slot)} – {addNinetyMin(slot)}
                    </div>
                    <div className={`rv2-slot-badge rv2-slot-badge--${status}`}>
                      <span className={`rv2-slot-dot rv2-slot-dot--${status}`} />
                      {status === "full"
                        ? "Complet"
                        : `${data.freeCourts} terrain${data.freeCourts > 1 ? "s" : ""} disponible${data.freeCourts > 1 ? "s" : ""}`}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

        </div>
      </div>
    </>
  );
}
